import { apiFetch as fetch } from "../lib/security";
import { CalendarCheck, Check, ExternalLink, Mail, MapPin, MessageCircle, Phone, Send, X } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/motion/Reveal";
import { RevealImage } from "../components/motion/RevealImage";
import { useAuth } from "../context/AuthContext";
import { siteInfo } from "../data/site";

async function readJson(response: Response) {
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.error?.message ?? "Unable to send inquiry.");
  }

  return data;
}

const contactSignals = [
  { icon: Phone, label: "Call for direct booking help" },
  { icon: CalendarCheck, label: "Ask about apartment availability" },
  { icon: MessageCircle, label: "Use chat for quick questions" },
];

export function Contact() {
  const { account, sessionToken, refreshAccount } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  useEffect(() => {
    if (!account) {
      return;
    }

    setName((value) => value || account.name);
    setEmail((value) => value || account.email);
    setPhone((value) => value || account.phone);
    setMarketingOptIn(account.marketingOptIn);
  }, [account]);

  const onContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormStatus("loading");
    setFormMessage("");

    try {
      const headers = new Headers({ "Content-Type": "application/json" });

      if (sessionToken) {
        headers.set("Authorization", `Bearer ${sessionToken}`);
      }

      await fetch("/api/contact", {
        method: "POST",
        headers,
        body: JSON.stringify({ name, email, phone, subject, message, website, marketingOptIn }),
      }).then(readJson);

      setFormStatus("success");
      setFormMessage("Inquiry received. We will reply directly.");
      setSubject("");
      setMessage("");
      setWebsite("");

      if (sessionToken) {
        await refreshAccount();
      }
    } catch (error) {
      setFormStatus("error");
      setFormMessage(error instanceof Error ? error.message : "Unable to send inquiry.");
    }
  };

  return (
    <>
      <SEO
        title="Contact | The Deposit Seed House"
        description="Call, map, directions, and inquiry form for The Deposit Seed House at 19 Center Street in Deposit, New York."
        image={siteInfo.exteriorImage}
        canonicalPath="/contact"
      />
      <section className="page-hero page-hero--contact">
        <Reveal>
          <p className="eyebrow">Contact</p>
          <h1>Questions before you book?</h1>
          <p>Reach the owner for booking questions, directions, stay details, apartment information, and direct confirmation.</p>
          <div className="page-hero__stats" aria-label="Contact options">
            {contactSignals.map((item) => {
              const Icon = item.icon;

              return (
                <span key={item.label}>
                  <Icon size={17} aria-hidden="true" />
                  {item.label}
                </span>
              );
            })}
          </div>
          <div className="page-hero__actions">
            <a className="button button--primary" href={siteInfo.phoneHref}>
              <Phone size={17} aria-hidden="true" />
              Call now
            </a>
            <a className="button button--light" href="#contact-form">
              Send inquiry
            </a>
          </div>
        </Reveal>
      </section>
      <section className="section contact-section">
        <Reveal className="contact-section-heading">
          <div>
            <p className="eyebrow">Direct support</p>
            <h2>Choose the fastest way to get an answer.</h2>
          </div>
          <p>Phone is best for time-sensitive booking questions. Use the form for detailed apartment, group, or longer-stay inquiries.</p>
        </Reveal>
        <div className="contact-layout">
          <Reveal className="contact-panel">
            <div className="contact-method">
              <Phone size={24} aria-hidden="true" />
              <div>
                <h2>Call</h2>
                <a href={siteInfo.phoneHref}>{siteInfo.phone}</a>
              </div>
            </div>
            <div className="contact-method">
              <MapPin size={24} aria-hidden="true" />
              <div>
                <h2>Visit</h2>
                <p>{siteInfo.address}</p>
                <a href={siteInfo.mapUrl} target="_blank" rel="noreferrer">
                  Map and directions <ExternalLink size={15} aria-hidden="true" />
                </a>
              </div>
            </div>
            <div className="contact-method">
              <Mail size={24} aria-hidden="true" />
              <div>
                <h2>Stay details</h2>
                <p>
                  Check-in: {siteInfo.checkIn}
                  <br />
                  Check-out: {siteInfo.checkOut}
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal className="contact-visual">
            <div className="frame--gilt">
              <RevealImage src={siteInfo.exteriorImage} alt="The Deposit Seed House building" />
            </div>
          </Reveal>
          <Reveal className="contact-form-card" id="contact-form">
            <h2>Send an inquiry</h2>
            <form className="react-contact-form" onSubmit={onContactSubmit}>
              <label>
                <span>Name</span>
                <input name="name" type="text" autoComplete="name" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} required />
              </label>
              <label>
                <span>Email</span>
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  maxLength={254}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  readOnly={Boolean(account)}
                  required
                />
              </label>
              <label>
                <span>Phone</span>
                <input name="phone" type="tel" autoComplete="tel" inputMode="tel" maxLength={40} value={phone} onChange={(event) => setPhone(event.target.value)} />
              </label>
              <label>
                <span>Subject</span>
                <input name="subject" type="text" maxLength={160} value={subject} onChange={(event) => setSubject(event.target.value)} required />
              </label>
              <label className="form-field-full">
                <span>Message</span>
                <textarea name="message" rows={5} maxLength={2000} value={message} onChange={(event) => setMessage(event.target.value)} required />
              </label>
              <label className="contact-checkbox form-field-full">
                <input type="checkbox" checked={marketingOptIn} onChange={(event) => setMarketingOptIn(event.target.checked)} />
                <span>
                  <strong>Email me discounts and stay updates</strong>
                  <small>Seasonal rates, openings, and Deposit travel notes.</small>
                </span>
              </label>
              <label className="verify-field" aria-hidden="true">
                <span>If you are human, leave this field blank.</span>
                <input name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} />
              </label>
              <button className="button button--primary" type="submit" disabled={formStatus === "loading"}>
                <Send size={18} aria-hidden="true" />
                {formStatus === "loading" ? "Sending" : "Send inquiry"}
              </button>
              {formStatus !== "idle" && formStatus !== "loading" && (
                <div className={`contact-form-message contact-form-message--${formStatus}`} role="status" aria-live="polite">
                  {formStatus === "success" ? <Check size={18} aria-hidden="true" /> : <X size={18} aria-hidden="true" />}
                  <span>{formMessage}</span>
                </div>
              )}
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
